import React, { useCallback, useEffect, useRef, useState } from "react";
import { masterApis } from "stores/master/masterApis";
import { dateTimeHelper } from "helper/dateTime";
import Stack from "components/common/Stack";
import Image from "components/common/Image";
import AppBar from "components/common/AppBar";
import { useHistory, useLocation } from "react-router-dom";
import { customerActions } from "stores/customer/customerActions";
import { useDispatch, useSelector } from "react-redux";
import { validationHelper } from "helper/validation";
import { commonActions } from "stores/common/commonActions";
import { localStore } from "stores/localStorage";
import CKYCmanual from "components/Onboarding/CKYCmanual";
import CKYCauto from "components/Onboarding/CKYCauto";
import JarvisButton from "components/JarvisButton";
import { config } from "config/index";
import { cameraHelper } from "helper/camera";
import { resolveCkycFormData } from "helper/ckycPrefill";
import {
  emptyCkycDocStatus,
  kycDocumentUploadSucceeded,
  mergeCkycDocStatusWithUploadFlags,
  ckycStatusFlagsFromProps,
  isKycDocumentUploaded,
  normalizeCkycDocStatus,
} from "helper/kycDocumentUpload";
import {
  applyCkycUploadFailure,
  applyCkycUploadResult,
  getCkycCustomerName,
  getKycUploadCatchMessage,
  isManualKycVerified,
  loadCkycCustomerName,
  requestCkycDocumentVerify,
  validateKycDocumentUpload,
} from "helper/ckycDocumentVerify";
import { jarvsiRMSOnboardingActions } from "stores/jarvisRMS/jarvisRMSOnboarding/jarvisRMSOnboardingAction";
import { onboardingActions } from "stores/onboarding/onboardingActions";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";

const ReKyc = () => {
  const [formData, setFormData] = useState({
    panNumber: "",
    aadharNumber: "",
    dateOfBirth: "",
    inputIdType: "C",
    otp: "",
  });

  const [manageForm, setManageForm] = useState({
    docType: "",
    docVerify: false,
  });

  const [formError, setFormError] = useState({
    aadharNumber: "",
    panNumber: "",
    dateOfBirth: "",
    panCard: "",
    aadharCardFront: "",
    aadharCardBack: "",
    otp: "",
  });

  const [imagesUploadingLoader, setImagesUploadingLoader] = useState({
    panCard: false,
    aadharCardFront: false,
    aadharCardBack: false,
  });

  const [isImagesUploaded, setIsImagesUploaded] = useState({
    panCard: false,
    aadharCardFront: false,
    aadharCardBack: false,
  });
  const [documentVerifyStatus, setDocumentVerifyStatus] = useState(
    emptyCkycDocStatus(),
  );
  const ckycUploadAppliedRef = useRef(false);

  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [enableManually, setEnableManually] = useState(true);
  const [hasFirstAttempError, setHasFirstAttempError] = useState(false);
  const [reKycButton, setReKycButton] = useState(false);
  const [isToShowDigioSignInButton, SetIsToShowDigioSignInButton] =
    useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlToken = searchParams.get("token");

  const token = urlToken || localStorage.getItem("token");
  const fromTokenLink =
    !!token && token !== "null" && token !== "undefined" && token.length > 0;
  // const customerId = useSelector((state) => state.common.customerId);

  const source = useSelector((state) => state.common.source);
  const isNaviaUser = source === "navia";

  const portfolioId = useSelector((state) => state.common.portfolioId);

  const loginProfile = useSelector((state) => state.customer.loginProfile);
  const rmsOnboarding = useSelector(
    (state) => state.jarvisRMS?.rmsOnboarding || {},
  );
  const portfolioOnboarding = useSelector((state) => state.onboarding || {});
  const phcOnboarding = useSelector((state) => state.PHC?.PHCOnboarding || {});

  const ckycDetailsSlice = isNaviaUser
    ? phcOnboarding
    : {
        isPanUploaded:
          rmsOnboarding.isPanUploaded || portfolioOnboarding.isPanUploaded,
        isAadhaarFrontUploaded:
          rmsOnboarding.isAadhaarFrontUploaded ||
          portfolioOnboarding.isAadhaarFrontUploaded,
        isAadhaarBackUploaded:
          rmsOnboarding.isAadhaarBackUploaded ||
          portfolioOnboarding.isAadhaarBackUploaded,
        panVerification:
          rmsOnboarding.panVerification || portfolioOnboarding.panVerification,
        aadharFrontVerification:
          rmsOnboarding.aadharFrontVerification ||
          portfolioOnboarding.aadharFrontVerification,
        aadharBackVerification:
          rmsOnboarding.aadharBackVerification ||
          portfolioOnboarding.aadharBackVerification,
      };

  const refreshCkycDetails = useCallback(() => {
    if (isNaviaUser) {
      dispatch(PHCOnboardingActions.getOnboardingData());
      return;
    }
    dispatch(jarvsiRMSOnboardingActions.getOnboardingData());
    dispatch(onboardingActions.getOnboardingData());
  }, [dispatch, isNaviaUser]);

  useEffect(() => {
    refreshCkycDetails();
  }, [refreshCkycDetails]);

  // Prefill PAN / DOB / Aadhaar from the PHC details API, same as PHCCkyc.
  // Waits for this visit's fetch and has no fallback to loginProfile or old form
  // values, so data persisted from another user in this browser is not shown.
  useEffect(() => {
    if (!isNaviaUser || !phcOnboarding.isInitialFetched) return;
    setFormData((prev) => ({
      ...prev,
      ...resolveCkycFormData(
        {},
        {
          panNumber: phcOnboarding.panNumber,
          dateOfBirth: phcOnboarding.dateOfBirth,
          aadharNumber: phcOnboarding.aadharNumber,
        },
      ),
    }));
  }, [
    isNaviaUser,
    phcOnboarding.isInitialFetched,
    phcOnboarding.panNumber,
    phcOnboarding.dateOfBirth,
    phcOnboarding.aadharNumber,
  ]);

  useEffect(() => {
    const uploadedFromDetails = (flag, verification) =>
      isKycDocumentUploaded(flag) || Boolean(normalizeCkycDocStatus(verification));

    setIsImagesUploaded((prev) => ({
      panCard:
        prev.panCard ||
        uploadedFromDetails(
          ckycDetailsSlice.isPanUploaded,
          ckycDetailsSlice.panVerification,
        ),
      aadharCardFront:
        prev.aadharCardFront ||
        uploadedFromDetails(
          ckycDetailsSlice.isAadhaarFrontUploaded,
          ckycDetailsSlice.aadharFrontVerification,
        ),
      aadharCardBack:
        prev.aadharCardBack ||
        uploadedFromDetails(
          ckycDetailsSlice.isAadhaarBackUploaded,
          ckycDetailsSlice.aadharBackVerification,
        ),
    }));
  }, [
    ckycDetailsSlice.isPanUploaded,
    ckycDetailsSlice.isAadhaarFrontUploaded,
    ckycDetailsSlice.isAadhaarBackUploaded,
    ckycDetailsSlice.panVerification,
    ckycDetailsSlice.aadharFrontVerification,
    ckycDetailsSlice.aadharBackVerification,
  ]);

  useEffect(() => {
    if (ckycUploadAppliedRef.current) {
      return;
    }
    setDocumentVerifyStatus((prev) =>
      mergeCkycDocStatusWithUploadFlags(
        prev,
        ckycStatusFlagsFromProps(ckycDetailsSlice),
      ),
    );
  }, [
    ckycDetailsSlice.panVerification,
    ckycDetailsSlice.aadharFrontVerification,
    ckycDetailsSlice.aadharBackVerification,
  ]);

  const handleChange = ({ name, value }) => {
    if (name === "docType") {
      setManageForm((prevManageForm) => ({ ...prevManageForm, [name]: value }));
    } else {
      setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
      setFormError((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleValidation = () => {
    const errors = {};

    // PAN (always)
    const pan = validationHelper.pan(formData.panNumber);
    if (!pan.isValid) errors.panNumber = pan.message;

    // DOB (always)
    const dob = validationHelper.dateOfBirth(formData.dateOfBirth);
    if (!dob.isValid) errors.dateOfBirth = dob.message;

    // Aadhaar (ONLY manual KYC)
    if (enableManually) {
      const aadhaar = validationHelper.aadhar(formData.aadharNumber);
      if (!aadhaar.isValid) errors.aadharNumber = aadhaar.message;
    }

    // Document images (ONLY manual KYC)
    if (
      enableManually &&
      (!isImagesUploaded.panCard ||
        !isImagesUploaded.aadharCardFront ||
        !isImagesUploaded.aadharCardBack)
    ) {
      dispatch(
        commonActions.setToast({
          message: "Please upload all required documents",
        }),
      );
      return false;
    }

    setFormError(errors);

    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (isNaviaUser) {
      loadCkycCustomerName(dispatch, getCkycCustomerName(loginProfile));
    } else if (fromTokenLink) {
      localStore.setToken(token);
      masterApis
        .getValidateToken()
        .then((response) => {
          if (response?.status === 200) {
            dispatch(customerActions.updateAuthStatus(true));
            dispatch(customerActions.updateFullName(response.name));
          }
        })
        .catch((err) => {
          console.log(err.message || "Error fetching status");
        });
    }
  }, [token, isNaviaUser, fromTokenLink]);

  const handleProceedDocumentSigningClick = async () => {
    if (isNaviaUser) {
      // Send user to Digio / document signing first
      history.push({
        pathname: "/customer/document-signing",
        state: {
          productCode: "PHC",
          returnURI: `/${encodeURIComponent(token)}/${encodeURIComponent(portfolioId)}`,
        },
      });
    } else {
      // Non-Navia users
      history.push({
        pathname: "/customer/document-signing",
        state: { productCode: "RE-SENTIMENT" },
      });
    }
  };

  const handleVerification = async () => {
    const isValid = handleValidation();
    if (!isValid) return;

    // if (fromTokenLink) {
    //   try {
    //     const res = await masterApis.verfyAutoKYC({
    //       dateOfBirth: dateTimeHelper.dateOfBirth(formData.dateOfBirth),
    //       pan: formData.panNumber,
    //     });

    //     if (isNaviaUser) {
    //       dispatch(
    //         commonActions.setNaviaDetail({
    //           isCkycDone: true,
    //           portfolioId: portfolioId,
    //           source: "navia",
    //           customerId: customerId,
    //         }),
    //       );

    //       // history.push({
    //         //   pathname: `/${token}/${portfolioId}`,

    //         // });
    //         dispatch(customerActions.updateAuthStatus(true));
    //         setIsVerified(true);

    //       if (res.code == "KYC009") {
    //         setIsVerified(true);
    //         SetIsToShowDigioSignInButton(true);
    //       } else {
    //         setHasFirstAttempError(true);
    //       }
    //     }
    //   } catch (e) {
    //     setHasFirstAttempError(true);
    //   } finally {
    //     setIsLoading(false);
    //   }
    // }

    if (enableManually) {
      setIsLoading(true);
      setHasFirstAttempError(false);
      setReKycButton(true);

      try {
        const res = await masterApis.verifyManualKyc({
          // DOB prefilled from the details API is a string, not a Date
          dateOfBirth: dateTimeHelper.dateOfBirth(new Date(formData.dateOfBirth)),
          panNumber: formData.panNumber,
          aadhaarNumber: formData.aadharNumber,
          productCode: isNaviaUser ? "PHC" : "ISRENEWAL",
        });

        if (!isManualKycVerified(res)) {
          dispatch(
            commonActions.setToast({
              message: res?.message || "Failed to verify",
            }),
          );
          return;
        }

        if (isNaviaUser) {
          dispatch(
            commonActions.setNaviaDetail({
              isCkycDone: true,
              portfolioId: portfolioId,
              source: "navia",
            }),
          );

          dispatch(customerActions.updateAuthStatus(true));
          setIsVerified(true);
        } else if (fromTokenLink) {
          setIsVerified(true);
        } else {
          dispatch(customerActions.getProfile());
          history.goBack();
          dispatch(customerActions.getProfile());
        }
      } catch (e) {
        setHasFirstAttempError(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onBrowseClick = async ({ type, data = null, source } = {}) => {
    const customerName = getCkycCustomerName(loginProfile);
    const fieldValidation = validateKycDocumentUpload({
      type,
      formData,
      customerName,
    });
    if (!fieldValidation.isValid) {
      setFormError((prev) => ({ ...prev, ...fieldValidation.errors }));
      if (fieldValidation.errors.customerName) {
        dispatch(
          commonActions.setToast({
            message: fieldValidation.errors.customerName,
          }),
        );
      }
      return;
    }

    let fileData = data;
    if (fileData?.name) {
      const fileExtension = fileData.name.split(".").pop().toLowerCase();
      fileData.format = fileExtension;
    }

    const isNativeCamera =
      config.platform !== "WEB" && source === "camera";
    if (isNativeCamera) {
      fileData = await cameraHelper.getKycNativeDocumentPhoto();
    }

    if (!fileData) return;

    let file;
    if (isNativeCamera) {
      file = await cameraHelper.getBlobFromCameraPhoto(fileData);
      if (!file || file.size === 0) {
        dispatch(
          commonActions.setToast({
            message: "Could not read the photo. Please try again.",
          }),
        );
        return;
      }
    } else {
      file = await readFileAsBlob(fileData);
      if (!file || file.size === 0) {
        dispatch(
          commonActions.setToast({
            message: "Could not read the selected file. Please try again.",
          }),
        );
        return;
      }
    }

    const kb = file.size / 1024;
    if (kb > 10000) {
      dispatch(
        commonActions.setToast({ message: "Image must be less than 10MB" }),
      );
      return;
    }

    setImagesUploadingLoader((prev) => ({ ...prev, [type]: true }));

    try {
      const res = await requestCkycDocumentVerify({
        type,
        file,
        fileName: `${type}_${loginProfile.customerId}_${Date.now()}`,
        originalFileName: fileData?.name,
        productCode: "PHC",
        loginProfile,
        formData,
      });

      const succeeded = kycDocumentUploadSucceeded(res, type);
      ckycUploadAppliedRef.current = true;
      const applied = applyCkycUploadResult(
        { formError, documentVerifyStatus },
        type,
        res,
      );
      setIsImagesUploaded((prev) => ({
        ...prev,
        [type]: succeeded,
      }));
      setFormError(applied.formError);
      setDocumentVerifyStatus(applied.documentVerifyStatus);
    } catch (e) {
      const applied = applyCkycUploadFailure(
        { formError, documentVerifyStatus },
        type,
        getKycUploadCatchMessage(e),
        e,
      );
      setIsImagesUploaded((prev) => ({ ...prev, [type]: false }));
      setFormError(applied.formError);
      setDocumentVerifyStatus(applied.documentVerifyStatus);
      dispatch(commonActions.setToast({ message: getKycUploadCatchMessage(e) }));
    } finally {
      setImagesUploadingLoader((prev) => ({ ...prev, [type]: false }));
    }
  };

  const readFileAsBlob = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) =>
        resolve(new Blob([e.target.result], { type: file.type }));
      reader.readAsArrayBuffer(file);
    });

  return (
    <>
      <AppBar color="inherit">
        <Stack sx={{ paddingLeft: 10, paddingY: "20px" }}>
          <Image src="/jarvis-logo.svg" width={100} height="auto" />
        </Stack>
      </AppBar>

      <Stack padding={10} sx={{ mt: 2, padding: { xs: "20px", md: 10 } }}>
        {/* {enableManually ? (
          <CKYCmanual
            formData={formData}
            formError={formError}
            isVerified={isVerified}
            isLoading={isLoading}
            hasFirstAttempError={hasFirstAttempError}
            isImagesUploaded={isImagesUploaded}
            imagesUploadingLoader={imagesUploadingLoader}
            handleChange={handleChange}
            toggleKYCtype={setEnableManually}
            isReKyc={true}
            reKycButton={reKycButton}
            isNavia={isNaviaUser}
            handleReVerification={handleVerification}
            onBrowseClick={onBrowseClick}
            //   onBrowseClick={({ type, data }) => {
            //   // Update uploaded image state dynamically
            //   setIsImagesUploaded((prev) => ({
            //     ...prev,
            //     [type]: true,
            //   }));
            // }}
          />
        ) 
        : (
          <CKYCauto
            formData={formData}
            formError={formError}
            isVerified={isVerified}
            isLoading={isLoading}
            hasFirstAttempError={hasFirstAttempError}
            handleChange={handleChange}
            handleVerification={handleVerification}
            toggleKYCtype={setEnableManually}
          />
        )
        } */}
        <CKYCmanual
          formData={formData}
          formError={formError}
          isVerified={isVerified}
          isLoading={isLoading}
          hasFirstAttempError={hasFirstAttempError}
          isImagesUploaded={isImagesUploaded}
          imagesUploadingLoader={imagesUploadingLoader}
          handleChange={handleChange}
          toggleKYCtype={setEnableManually}
          isReKyc={true}
          reKycButton={reKycButton}
          isNavia={isNaviaUser}
          handleReVerification={handleVerification}
          onBrowseClick={onBrowseClick}
          documentVerifyStatus={documentVerifyStatus}
        />

        {isVerified ? (
          <Stack
            direction="row"
            justifyContent={{ xs: "center", md: "flex-start" }}
            mb={3}
            mt={6}
            alignItems="center"
            sx={{ width: { xs: "100%", md: "auto" }, background: "#fff" }}
          >
            <Stack
              sx={{
                position: { xs: "fixed", md: "unset" },
                bottom: {
                  xs: "calc(10px + var(--safe-area-inset-bottom)) !important",
                  md: "unset",
                },
                width: { xs: "90vw", md: "unset" },
              }}
            >
              <JarvisButton
                loading={isLoading}
                onClick={handleProceedDocumentSigningClick}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                Proceed to Client Agreement
              </JarvisButton>
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </>
  );
};

export default ReKyc;
